import { useEffect } from "react";
import PalmBeachHeroSection from "./components/HeroSection";
import PalmBeachStickyNav from "./components/StickyNav";
import PalmBeachProcessingTimeSection from "./components/ProcessingTimeSection";
import PalmBeachServicesSection from "./components/ServicesSection";
import PalmBeachLocalAreaSection from "./components/LocalAreaSection";
import PalmBeachTravelTimingSection from "./components/TravelTimingSection";
import PalmBeachDestinationsSection from "./components/DestinationsSection";
import PalmBeachTestimonialsSection from "./components/TestimonialsSection";
import PalmBeachAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import PalmBeachComparisonSection from "./components/ComparisonSection";
import PalmBeachMistakesSection from "./components/MistakesSection";
import PalmBeachHowItWorksSection from "./components/HowItWorksSection";
import PalmBeachFAQSection, { faqs } from "./components/FAQSection";
import PalmBeachHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import PalmBeachFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function PalmBeachPage() {
  useEffect(() => {
    document.title = "Emergency Passport Palm Beach, FL — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/palm-beach/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Palm Beach, FL — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving South Florida travelers.");
    setMeta("geo.placename", "Palm Beach, Florida");
    setMeta("geo.region", "US-FL");
    setMeta("geo.position", "26.7056;-80.0364");
    setMeta("ICBM", "26.7056, -80.0364");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Florida", item: "https://fastpassportcenter.com/florida/" },
          { "@type": "ListItem", position: 3, name: "Palm Beach Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center does not have a physical office in Palm Beach — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for Palm Beach and South Florida travelers.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: "Palm Beach",
          containedInPlace: {
            "@type": "State",
            name: "Florida",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: "Palm Beach",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <PalmBeachHeroSection />
      <PalmBeachStickyNav />
      <PalmBeachProcessingTimeSection />
      <PalmBeachServicesSection />
      <PalmBeachLocalAreaSection />
      <PalmBeachTravelTimingSection />
      <PalmBeachDestinationsSection />
      <PalmBeachTestimonialsSection />
      <PalmBeachAcceptanceFacilitiesSection />
      <PalmBeachComparisonSection />
      <PalmBeachMistakesSection />
      <PalmBeachHowItWorksSection />
      <PalmBeachFAQSection />
      <PalmBeachHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/palm-beach" state="FL" />
      <PalmBeachFinalCTA />
    </div>
  );
}